import { toast } from "react-toastify"
import ResourceBudgetAPI from "../../../../api/resourceBudget"
import { primaryTabs } from "../../../pages/ResourceBudget/constants"
import { setSavedData } from "../../../redux/slices/resourceBudgetSlice"
import { setLoading } from "../../../redux/slices/appSlice"
import {
  setForecastData,
  setForecastLoading,
} from "../../../redux/slices/forecastSlice"
import { useResponseHandle } from "../../../hooks"

const API = new ResourceBudgetAPI()

export const getProviders = async (params: any) => {
  const { office, dispatch, loading, token, secondaryIndex } = params

  if (token && office?.practice_sk) {
    loading(true)
    let practiceSk = office?.practice_sk
    const providerType = secondaryIndex ? "hygienist" : "dentist"
    const response = await API.getProviders({ token, practiceSk, providerType })
    if (
      response?.data?.status_code != 200 &&
      response?.data?.status_code != 401
    ) {
      toast.error(response?.data.display_message)
    } else {
      dispatch(response.data.data)
    }
  }
  loading(false)
}

export const getRoles = async (params: any) => {
  const { dispatch, loading, token } = params

  if (token) {
    loading(true)
    const response = await API.getNonClinicalRoles(token)
    if (
      response?.data?.status_code != 200 &&
      response?.data?.status_code != 401
    ) {
      toast.error(response?.data.display_message)
    } else {
      dispatch(response.data.data)
    }
    loading(false)
  }
}

export const setCopyOptions = async (params: any) => {
  const { dispatch, token, loading, office, year, month } = params
  const { copyTo, primaryIndex, secondaryIndex } = params?.state

  if (token) {
    loading(true)
    const isClinical = primaryIndex === 0
    const providerType = secondaryIndex ? "hygienist" : "dentist"

    const args = {
      practiceSk: office?.practice_sk,
      year: year?.label,
      providerType: isClinical ? providerType : null,
      month: month,
      isClinical,
    }
    const payload = {
      months_list: copyTo?.selected,
    }
    const response = await API.setCopyOptions({ token, args, payload })

    if (
      response?.data?.status_code != 200 &&
      response?.data?.status_code != 401
    ) {
      toast.error(response?.data.display_message)
    } else {
      toast.success(`Budget copied to selected months successfully!`)
      dispatch()
    }
    loading(false)
  }
}

// Enhanced createProvider with better error handling and validation
const createProvider = async (params: any) => {
  const { setLoading, stopLoading, setSaved, isBudget, clinical } = params
  const { token, provider, apiEndpoint, office, year, month } = params

  console.log({ params })

  // Enhanced validation before API call
  if (!token) {
    toast.error("Authentication required. Please log in again.")
    return
  }

  if (
    !provider ||
    (!provider.firstname && !provider.lastname && !provider.resource_name)
  ) {
    toast.error("Please select a provider before saving.")
    return
  }

  if (!office?.practice_sk) {
    toast.error("Office information is missing. Please refresh and try again.")
    return
  }

  if (!year?.label) {
    toast.error("Year selection is required.")
    return
  }

  try {
    setLoading()
    const { id, ...providerWithoutId } = provider
    const payload = {
      practice_sk: office?.practice_sk,
      year: year?.label,
      month: month,
      ...providerWithoutId,
    }

    const response = await apiEndpoint({ token, payload, published: isBudget })

    if (
      response?.data?.status_code != 200 &&
      response?.data?.status_code != 401
    ) {
      const errorMessage =
        response?.data?.display_message ||
        "Failed to add provider. Please try again."
      toast.error(errorMessage)
      console.error("Provider creation failed:", response?.data)
    } else {
      setSaved({ ...provider, ...response?.data?.data })

      const table = isBudget ? "budget" : "forecast"
      if (clinical) {
        toast.success(
          `Provider ${provider?.firstname} ${provider?.lastname} added to ${table} successfully`,
        )
      } else {
        toast.success(
          `Staff ${provider?.resource_name} added to ${table} successfully`,
        )
      }
    }
  } catch (error) {
    console.error("Provider creation error:", error)
    toast.error("Network error. Please check your connection and try again.")
  } finally {
    stopLoading()
  }
}

const updateProvider = async (params: any) => {
  const { setLoading, stopLoading, setSaved, isBudget } = params
  const { provider, token, apiEndpoint, primaryIndex } = params
  const { office, month, year } = params

  // Enhanced validation before API call
  if (!token) {
    toast.error("Authentication required. Please log in again.")
    return
  }

  if (!provider) {
    toast.error("Provider data is missing. Please refresh and try again.")
    return
  }

  if (!office?.practice_sk) {
    toast.error("Office information is missing. Please refresh and try again.")
    return
  }

  if (!year?.label) {
    toast.error("Year selection is required.")
    return
  }

  try {
    setLoading()
    const args = {
      practiceSk: office?.practice_sk,
      year: year?.label,
      published: isBudget,
    }
    const payload = {
      month,
      ...provider,
    }

    const response = await apiEndpoint({ token, payload, args })

    if (
      response?.data?.status_code != 200 &&
      response?.data?.status_code != 401
    ) {
      const errorMessage =
        response?.data?.display_message ||
        "Failed to update provider. Please try again."
      toast.error(errorMessage)
      console.error("Provider update failed:", response?.data)
    } else {
      setSaved()

      // Trigger data refresh after successful provider update
      if (params.refreshData) {
        try {
          await params.refreshData()
        } catch (refreshError) {
          console.error(
            "Failed to refresh data after provider update:",
            refreshError,
          )
        }
      }

      const providerType = primaryIndex ? "Staff" : "Provider"
      toast.success(
        `${providerType} ${provider?.resource_name} updated from ${
          isBudget ? "budget" : "forecast"
        } successfully!`,
      )
    }
  } catch (error) {
    console.error("Provider update error:", error)
    toast.error("Network error. Please check your connection and try again.")
  } finally {
    stopLoading()
  }
}

// New function for bulk updates with array payload
const bulkUpdateProvider = async (params: any) => {
  const { setLoading, stopLoading, setSaved, isBudget } = params
  const { changes, token, apiEndpoint, primaryIndex } = params
  const { office, month, year } = params

  if (token) {
    setLoading()
    const args = {
      practiceSk: office?.practice_sk,
      year: year?.label,
      published: isBudget,
    }

    // Transform changes to array format with month, removing metadata
    // If no changes, send empty array
    const payload =
      changes && changes.length > 0
        ? changes.map((change: any) => {
            // Remove metadata fields that shouldn't be sent to API
            const { timestamp, tabKey, ...cleanChange } = change
            return {
              month,
              ...cleanChange, // This now contains the complete row object
            }
          })
        : [] // Send empty array if no changes

    console.log("Sending payload to API:", payload)
    console.log("API args:", args)

    const response = await apiEndpoint({ token, payload, args })

    console.log("API response:", response)

    // Return the response for the calling function to check
    return response
  }

  // Return null if no token
  return null
}

const deleteProvider = async (params: any) => {
  const { loading, setSaved, provider, isBudget, year, onSuccess } = params
  const { token, apiEndpoint, primaryIndex, office } = params

  if (token) {
    loading(true)
    const args = {
      practiceSk: office?.practice_sk,
      year: year?.label,
      id: provider?.id,
      published: isBudget,
    }

    const response = await apiEndpoint({ token, args })
    
    if (
      response?.data?.status_code != 200 &&
      response?.data?.status_code != 401
    ) {
      toast.error(response?.data.display_message)
    } else {
      setSaved(provider?.id)
      const providerType = primaryIndex ? "Staff" : "Provider"
      toast.success(
        `${providerType} ${provider?.resource_name} deleted from ${
          isBudget ? "budget" : "forecast"
        } successfully!`,
      )
      // Call onSuccess callback if provided (for showing sync popup)
      if (onSuccess && typeof onSuccess === 'function') {
        onSuccess()
      }
    }
    loading(false)
  }
}

const getCopyOptions = async (params: any) => {
  const { dispatch, token, loading, apiEndpoint } = params
  const { office, year } = params
  if (token) {
    loading(true)
    const args = {
      practiceSk: office?.practice_sk,
      year: year?.label,
    }
    const response = await apiEndpoint({ token, args })
    if (
      response?.data?.status_code != 200 &&
      response?.data?.status_code != 401
    ) {
      toast.error(response?.data.display_message)
    } else {
      dispatch(response?.data?.data)
    }
    loading(false)
  }
}

const getResourceData = async (params: any) => {
  const { dispatch, token, loading, secondaryIndex, isForecast } = params
  const { office, year, month, apiEndpoint } = params
  if (token) {
    loading(true)
    const args = {
      practiceSk: office?.practice_sk,
      providerType: secondaryIndex ? "hygienist" : "dentist",
      year: year?.label,
      month: month,
      isForecast,
    }

    const response = await apiEndpoint({ token, args })
    if (
      response?.data?.status_code != 200 &&
      response?.data?.status_code != 401
    ) {
      toast.error(response?.data.display_message)
    } else {
      // Pass the secondaryIndex along with the data to identify which tab this belongs to
      dispatch({
        ...response?.data?.data,
        _secondaryIndex: secondaryIndex,
      })
    }
    loading(false)
  }
}

export const syncBudget = async (params: any) => {
  const { year, loading, token, setError } = params
  const { navigate, office } = params

  if (token) {
    loading(true)
    const args = {
      practiceSk: office?.practice_sk,
      year: year?.label,
    }
    const response = await API.syncBudgetToPnl({ token, args })

    const result = useResponseHandle({
      apiResponse: response,
      successMessage: false,
    })
    if (result?.isAuthorized) {
      if (result?.statusCode == 200) {
        toast.success(
          `Budget of '${office?.label || ""}' office for year ${
            args.year
          } synced successfully`,
        )
      } else {
        setError(response?.data.display_message)
      }
      loading(false)
    } else {
      loading(false)
      navigate("/auth")
    }
  }
}

// New function to save all changes from multiple tabs
export const saveAllChanges = async (params: any) => {
  const {
    editState,
    loading,
    token,
    setError,
    onSuccess,
    office,
    year,
    month,
    isBudget = true,
  } = params

  if (!token) {
    return
  }

  // Collect all changes from all tabs
  // const clinicalDoctorsChanges = editState.clinicalDoctors.data || []
  const clinicalDoctorsChanges = Object.values(editState?.monthStates)
  .flatMap(
    (month: any) => month?.clinicalDoctors?.data ?? []
  );
  // const clinicalHygienistsChanges = editState.clinicalHygienists.data || []
  // const clinicalHygienistsChanges=


  const clinicalHygienistsChanges = Object.values(editState?.monthStates)
  .flatMap(
    (month: any) => month?.clinicalHygienists?.data ?? []
  );

  const nonClinicalChanges =  Object.values(editState?.monthStates)
  .flatMap(
    (month: any) => month?.nonClinical?.data ?? []
  ) || []
  const allChanges = editState.allChanges || []

  const totalChanges =
    clinicalDoctorsChanges.length +
    clinicalHygienistsChanges.length +
    nonClinicalChanges.length +
    allChanges.length

  if (totalChanges === 0) {
    toast.info("No changes to save")
    return
  }

  loading(true)

  try {
    const promises = []

    // Always make API call for clinical doctors (even if no changes, send empty array)
    const clinicalDoctorsParams = {
      changes: clinicalDoctorsChanges,
      token,
      apiEndpoint: API.bulkUpdateClinicalData,
      primaryIndex: 0, // Clinical
      office,
      year,
      month,
      isBudget,
      setLoading: () => {}, // No individual loading for bulk
      stopLoading: () => {},
      setSaved: () => {},
    }
    promises.push(
      genericBulkUpdate[primaryTabs.clinical](clinicalDoctorsParams),
    )

    // Always make API call for clinical hygienists (even if no changes, send empty array)
    const clinicalHygienistsParams = {
      changes: clinicalHygienistsChanges,
      token,
      apiEndpoint: API.bulkUpdateClinicalData,
      primaryIndex: 0, // Clinical
      office,
      year,
      month,
      isBudget,
      setLoading: () => {}, // No individual loading for bulk
      stopLoading: () => {},
      setSaved: () => {},
    }
    promises.push(
      genericBulkUpdate[primaryTabs.clinical](clinicalHygienistsParams),
    )

    // Always make API call for non-clinical (even if no changes, send empty array)
    const nonClinicalParams = {
      changes: nonClinicalChanges,
      token,
      apiEndpoint: API.bulkUpdateNonClinicalData,
      primaryIndex: 1, // Non-clinical
      office,
      year,
      month,
      isBudget,
      setLoading: () => {}, // No individual loading for bulk
      stopLoading: () => {},
      setSaved: () => {},
    }
    promises.push(genericBulkUpdate[primaryTabs.nonClinical](nonClinicalParams))

    // Wait for all API calls to complete
    const results = await Promise.all(promises)

    // Filter out null results (no changes to save)
    const validResults = results.filter((result: any) => result !== null)

    // Debug: Log the results to see what we're getting
    console.log("API Results:", validResults)

    // Check if all calls were successful using the standard response handler
    const allSuccessful = validResults.every((result: any) => {
      console.log("Checking result:", result)

      const responseResult = useResponseHandle({
        apiResponse: result,
        successMessage: false,
        hideError: true,
      })

      console.log("Response result:", responseResult)

      return responseResult.isAuthorized && responseResult.statusCode === 200
    })

    // We should have exactly 3 API calls (clinical doctors, clinical hygienists, non-clinical)
    if (allSuccessful && validResults.length === 3) {
      console.log("All POST APIs successful, making additional API calls...")

      // Make additional API calls after successful POST operations
      try {
        // Example: Refresh data or sync operations
        const additionalPromises: Promise<any>[] = []

        // Add any additional API calls here
        // For example:
        // additionalPromises.push(refreshDataAPI({ token, office, year, month }))
        // additionalPromises.push(syncToPnlAPI({ token, office, year }))
        // additionalPromises.push(updateAggregationsAPI({ token, office, year, month }))

        if (additionalPromises.length > 0) {
          const additionalResults = await Promise.all(additionalPromises)
          console.log("Additional API results:", additionalResults)
        }

        // Call sync to P&L API after successful budget updates
        console.log("Syncing budget to P&L after successful save...")
        try {
          const syncArgs = {
            practiceSk: office?.practice_sk,
            year: year?.label,
          }
          const syncResponse = await API.syncBudgetToPnl({
            token,
            args: syncArgs,
          })

          const syncResult = useResponseHandle({
            apiResponse: syncResponse,
            successMessage: false,
          })

          if (syncResult?.isAuthorized && syncResult?.statusCode === 200) {
            console.log("Successfully synced to P&L")
            toast.success(
              `Successfully saved ${totalChanges} changes and synced to P&L!`,
            )
          } else {
            console.log("Sync to P&L failed, but save was successful")
            toast.success(`Successfully saved ${totalChanges} changes!`)
          }
        } catch (syncError) {
          console.error("Sync to P&L error:", syncError)
          toast.success(`Successfully saved ${totalChanges} changes!`)
        }

        console.log("Clearing dirty cells after successful save...")
        if (onSuccess) {
          onSuccess() // This calls syncToPNL() which clears all dirty cells
        }
      } catch (additionalError) {
        console.error("Additional API calls failed:", additionalError)
        // Still show success for the main save operation
        toast.success(`Successfully saved ${totalChanges} changes!`)
        if (onSuccess) {
          onSuccess()
        }
      }
    } else if (validResults.length === 0) {
      toast.info("No changes to save")
    } else {
      console.error("Some API calls failed:", validResults)
      console.error("Expected 3 API calls, got:", validResults.length)
      setError("Some changes failed to save. Please try again.")
    }
  } catch (error) {
    console.error("Error saving changes:", error)
    setError("Failed to save changes. Please try again.")
  } finally {
    loading(false)
  }
}

// New function to save all forecast changes from multiple tabs
export const saveAllForecastChanges = async (params: any) => {
  const {
    editState,
    loading,
    token,
    setError,
    onSuccess,
    office,
    year,
    month,
    isBudget = false, // Forecast is not budget
  } = params

  if (!token) {
    return
  }

  // Collect all changes from all tabs
  const clinicalDoctorsChanges = editState.clinicalDoctors.data || []
  const clinicalHygienistsChanges = editState.clinicalHygienists.data || []
  const nonClinicalChanges = editState.nonClinical.data || []
  const allChanges = editState.allChanges || []

  const totalChanges =
    clinicalDoctorsChanges.length +
    clinicalHygienistsChanges.length +
    nonClinicalChanges.length +
    allChanges.length

  if (totalChanges === 0) {
    toast.info("No changes to save")
    return
  }

  loading(true)

  try {
    const promises = []

    // Always make API call for clinical doctors (even if no changes, send empty array)
    const clinicalDoctorsParams = {
      changes: clinicalDoctorsChanges,
      token,
      apiEndpoint: API.bulkUpdateClinicalData,
      primaryIndex: 0, // Clinical
      office,
      year,
      month,
      isBudget, // false for forecast
      setLoading: () => {}, // No individual loading for bulk
      stopLoading: () => {},
      setSaved: () => {},
    }
    promises.push(
      genericBulkUpdate[primaryTabs.clinical](clinicalDoctorsParams),
    )

    // Always make API call for clinical hygienists (even if no changes, send empty array)
    const clinicalHygienistsParams = {
      changes: clinicalHygienistsChanges,
      token,
      apiEndpoint: API.bulkUpdateClinicalData,
      primaryIndex: 0, // Clinical
      office,
      year,
      month,
      isBudget, // false for forecast
      setLoading: () => {}, // No individual loading for bulk
      stopLoading: () => {},
      setSaved: () => {},
    }
    promises.push(
      genericBulkUpdate[primaryTabs.clinical](clinicalHygienistsParams),
    )

    // Always make API call for non-clinical (even if no changes, send empty array)
    const nonClinicalParams = {
      changes: nonClinicalChanges,
      token,
      apiEndpoint: API.bulkUpdateNonClinicalData,
      primaryIndex: 1, // Non-clinical
      office,
      year,
      month,
      isBudget, // false for forecast
      setLoading: () => {}, // No individual loading for bulk
      stopLoading: () => {},
      setSaved: () => {},
    }
    promises.push(genericBulkUpdate[primaryTabs.nonClinical](nonClinicalParams))

    // Wait for all API calls to complete
    const results = await Promise.all(promises)

    // Filter out null results (no changes to save)
    const validResults = results.filter((result: any) => result !== null)

    // Debug: Log the results to see what we're getting
    console.log("Forecast API Results:", validResults)

    // Check if all calls were successful using the standard response handler
    const allSuccessful = validResults.every((result: any) => {
      console.log("Checking forecast result:", result)

      const responseResult = useResponseHandle({
        apiResponse: result,
        successMessage: false,
        hideError: true,
      })

      console.log("Forecast response result:", responseResult)

      return responseResult.isAuthorized && responseResult.statusCode === 200
    })

    // We should have exactly 3 API calls (clinical doctors, clinical hygienists, non-clinical)
    if (allSuccessful && validResults.length === 3) {
      console.log("All forecast POST APIs successful")

      toast.success(`Successfully saved ${totalChanges} forecast changes!`)

      console.log("Clearing dirty cells after successful save...")
      if (onSuccess) {
        onSuccess() // This calls syncForecastToPNL() which clears all dirty cells
      }
    } else if (validResults.length === 0) {
      toast.info("No changes to save")
    } else {
      console.error("Some forecast API calls failed:", validResults)
      console.error("Expected 3 API calls, got:", validResults.length)
      setError("Some changes failed to save. Please try again.")
    }
  } catch (error) {
    console.error("Error saving forecast changes:", error)
    setError("Failed to save changes. Please try again.")
  } finally {
    loading(false)
  }
}

export const publishBudget = async (params: any) => {
  const { dispatch, loading, token, setError } = params
  const { navigate, office, year } = params

  if (token) {
    loading(true)
    const args = {
      practiceSk: office?.practice_sk,
      year: year?.label,
    }
    const publishResponse = await API.postPublishBudget({ token, args })

    const result = useResponseHandle({
      apiResponse: publishResponse,
      successMessage: false,
      hideError: true,
    })
    if (result?.isAuthorized) {
      if (result?.statusCode == 200) {
        const syncResponse = await API.syncBudgetToPnl({ token, args })
        const syncResult = useResponseHandle({
          apiResponse: syncResponse,
          successMessage: false,
          hideError: true,
        })
        if (syncResult?.isAuthorized) {
          if (syncResult?.statusCode === 200) {
            toast.success(
              `Budget of '${office?.label || ""}' office for year ${
                args.year || ""
              } published successfully`,
            )
          } else {
            setError("Budget published but sync failed try again.")
          }
          dispatch()
          loading(false)
        } else {
          loading(false)
          navigate("/auth")
        }
      } else {
        setError(publishResponse?.data.display_message)
      }
      loading(false)
    } else {
      loading(false)
      navigate("/auth")
    }
  }
}
// Forecast Budget
export const genericForecastGet = {
  [primaryTabs.clinical]: (params: any) => {
    const { dispatch } = params
    getResourceData({
      ...params,
      isForecast: true,
      dispatch: (data: any) => dispatch(setForecastData(data)),
      loading: (loadVal: boolean) => dispatch(setForecastLoading(loadVal)),
      apiEndpoint: API.getClinicalData,
    })
  },
  [primaryTabs.nonClinical]: (params: any) => {
    const { dispatch } = params
    getResourceData({
      ...params,
      isForecast: true,
      dispatch: (data: any) => dispatch(setForecastData(data)),
      loading: (loadVal: boolean) => dispatch(setForecastLoading(loadVal)),
      apiEndpoint: API.getNonClinicalData,
    })
  },
}
export const genericDelete = {
  [primaryTabs.clinical]: (params: any) =>
    deleteProvider({
      ...params,
      apiEndpoint: API.deleteClinicalData,
    }),
  [primaryTabs.nonClinical]: (params: any) =>
    deleteProvider({
      ...params,
      apiEndpoint: API.deleteNonClinicalData,
    }),
}

// Resource Budget
export const genericGet = {
  [primaryTabs.clinical]: (params: any) => {
    const { dispatch } = params
    getResourceData({
      ...params,
      dispatch: (data: any) => dispatch(setSavedData(data)),
      loading: (loadVal: boolean) => dispatch(setLoading(loadVal)),
      apiEndpoint: API.getClinicalData,
    })
  },
  [primaryTabs.nonClinical]: (params: any) => {
    const { dispatch } = params
    getResourceData({
      ...params,
      dispatch: (data: any) => dispatch(setSavedData(data)),
      loading: (loadVal: boolean) => dispatch(setLoading(loadVal)),
      apiEndpoint: API.getNonClinicalData,
    })
  },
}
export const genericCreate = {
  [primaryTabs.clinical]: (params: any) =>
    createProvider({
      ...params,
      clinical: true,
      apiEndpoint: API.createClinicalProvider,
    }),
  [primaryTabs.nonClinical]: (params: any) =>
    createProvider({
      ...params,
      apiEndpoint: API.createNonClinicalProvider,
    }),
}
export const genericUpdate = {
  [primaryTabs.clinical]: (params: any) =>
    updateProvider({
      ...params,
      apiEndpoint: API.updateClinicalData,
    }),
  [primaryTabs.nonClinical]: (params: any) =>
    updateProvider({
      ...params,
      apiEndpoint: API.updateNonClinicalData,
    }),
}

// New generic functions for bulk updates
export const genericBulkUpdate = {
  [primaryTabs.clinical]: (params: any) =>
    bulkUpdateProvider({
      ...params,
      apiEndpoint: API.bulkUpdateClinicalData,
    }),
  [primaryTabs.nonClinical]: (params: any) =>
    bulkUpdateProvider({
      ...params,
      apiEndpoint: API.bulkUpdateNonClinicalData,
    }),
}
export const copyOptions = {
  [primaryTabs.clinical]: (params: any) =>
    getCopyOptions({
      ...params,
      apiEndpoint: API.getClinicalCopyOptions,
    }),
  [primaryTabs.nonClinical]: (params: any) =>
    getCopyOptions({
      ...params,
      apiEndpoint: API.getNonClinicalCopyOptions,
    }),
}
